import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Copy, ExternalLink, PlugZap, Send, XCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Area, Card, Field, ImageField, Input, OBtn, PageTitle, Select, Skel, Toggle, YBtn, useSaver } from "@/admin/ui";
import { adminApi, useAdmin, useRefresh } from "@/admin/lib";
import { API } from "@/lib/api";

const useSettingsForm = () => {
  const { data } = useAdmin("/admin/settings");
  const [s, setS] = useState(null);
  const [saving, run] = useSaver();
  const refresh = useRefresh();
  const qc = useRefresh();
  useEffect(() => { if (data) setS(data); }, [data]);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async (fields) => {
    const body = fields ? Object.fromEntries(fields.map((k) => [k, s[k]])) : s;
    const r = await run(() => adminApi.put("/admin/settings", body), "Settings saved");
    if (r) { setS(r.data); refresh(); qc(); }
  };
  return { s, set, save, saving };
};

const F = ({ s, set, k, label, type, hint, area, testId }) => (
  <Field label={label} hint={hint}>
    {area ? <Area value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} testId={testId || `setting-${k}`} rows={4} />
      : <Input type={type || "text"} value={s[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} testId={testId || `setting-${k}`} />}
  </Field>
);

const EVENTS = [["PageView", "Every page view"], ["Lead", "Successful form submit"], ["Contact", "WhatsApp button click"], ["ViewContent", "Product detail page"], ["InitiateCheckout", "Checkout opened"], ["Purchase", "Razorpay payment success (value + INR)"]];

export function Marketing() {
  const { s, set, save, saving } = useSettingsForm();
  const { data: ready, refetch } = useAdmin("/admin/readiness");
  if (!s) return <Skel rows={8} />;
  const active = !!s.facebook_pixel_id && s.facebook_pixel_enabled !== false;
  const p = { s, set };
  const doSave = async () => { await save(["facebook_pixel_id", "facebook_pixel_enabled", "ga4_id", "gtm_id", "gsc_verification", "fb_domain_verification", "custom_head_scripts", "custom_footer_scripts"]); refetch(); };
  return (
    <>
      <PageTitle title="Marketing & Tracking" subtitle={s.require_cookie_consent ? "Cookie consent is ON: tracking scripts load only after visitors accept cookies." : "Cookie consent is OFF: tracking scripts load for every visitor."}>
        <YBtn loading={saving} onClick={doSave} testId="marketing-save-btn">Save</YBtn>
      </PageTitle>
      <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          <Card title="Facebook (Meta) Pixel">
            <div className="space-y-4">
              <F {...p} k="facebook_pixel_id" label="Pixel ID" hint="Meta Events Manager → Data Sources → select your Pixel → copy the numeric Pixel ID." />
              <Toggle label="Enable Pixel" hint="Injects the Pixel into <head> of all public pages" checked={s.facebook_pixel_enabled !== false} onChange={(v) => set("facebook_pixel_enabled", v)} testId="setting-facebook_pixel_enabled" />
              <div className="rounded-xl bg-page p-4 text-xs leading-relaxed text-body">
                <b className="text-navy">How to test:</b> install the <a className="font-bold text-ocean" href="https://chromewebstore.google.com/detail/meta-pixel-helper/fdgfkebogiimcoedlicjlajpkdmockpc" target="_blank" rel="noopener noreferrer">Meta Pixel Helper</a> Chrome extension, open your site, accept cookies, and the helper will list the events firing. You can also use Events Manager → Test events.
              </div>
              <div data-testid="pixel-events-checklist">
                <p className="label-base">Standard events</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {EVENTS.map(([e, d]) => (
                    <div key={e} className="flex items-center gap-2.5 rounded-xl border border-line px-3 py-2.5" data-testid={`event-${e}`}>
                      {active ? <CheckCircle2 className="h-4 w-4 shrink-0 text-success" /> : <XCircle className="h-4 w-4 shrink-0 text-muted2" />}
                      <div><p className="text-sm font-bold text-navy">{e}</p><p className="text-xs text-muted2">{d}</p></div>
                    </div>
                  ))}
                </div>
                {!active && <p className="mt-2 text-xs text-muted2">Events become active once a Pixel ID is saved and enabled.</p>}
              </div>
            </div>
          </Card>
          <Card title="Google & verification">
            <div className="grid gap-4 sm:grid-cols-2">
              <F {...p} k="ga4_id" label="GA4 Measurement ID" hint="e.g. G-XXXXXXXXXX" />
              <F {...p} k="gtm_id" label="Google Tag Manager ID" hint="e.g. GTM-XXXXXXX" />
              <F {...p} k="gsc_verification" label="Google Search Console verification" hint="Paste the meta tag or just its content value" />
              <F {...p} k="fb_domain_verification" label="Facebook domain verification" hint="Paste the meta tag or its content value" />
            </div>
          </Card>
          <Card title="Custom scripts">
            <div className="space-y-4">
              <F {...p} k="custom_head_scripts" label="Custom header scripts" area hint="Inserted into <head> (after marketing consent)" />
              <F {...p} k="custom_footer_scripts" label="Custom footer scripts" area hint="Inserted before </body> (after marketing consent)" />
            </div>
          </Card>
        </div>
        <Card title="Facebook Ads readiness" testId="ads-readiness" className="xl:sticky xl:top-6 xl:self-start">
          <div className="space-y-2">
            {(ready || []).map((r) => (
              <div key={r.key} className="flex items-center gap-2.5 rounded-xl border border-line px-3 py-2.5" data-testid={`readiness-${r.key}`}>
                {r.ok ? <CheckCircle2 className="h-5 w-5 shrink-0 text-success" /> : <XCircle className="h-5 w-5 shrink-0 text-red-500" />}
                <p className="flex-1 text-sm font-semibold text-navy">{r.label}</p>
                {!r.ok && <Link to={r.fix} className="text-xs font-bold text-ocean" data-testid={`readiness-fix-${r.key}`}>Fix</Link>}
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted2">{(ready || []).filter((r) => r.ok).length}/{(ready || []).length} checks passed</p>
        </Card>
      </div>
    </>
  );
}

const BIZ = [["brand_name", "Brand name"], ["legal_name", "Legal name"], ["business_type", "Business type"], ["owner_name", "Owner name"], ["business_category", "Business category"], ["website_url", "Website URL"], ["email", "Email"], ["phone", "Phone"], ["whatsapp", "WhatsApp number"], ["support_hours", "Support hours"], ["gstin", "GSTIN (shown publicly if filled)"], ["pan", "PAN (internal only, never public)"], ["grievance_officer_name", "Grievance officer name"], ["grievance_officer_email", "Grievance officer email"], ["jurisdiction_city", "Jurisdiction city"], ["retention_days", "Lead retention (days)", "number"], ["gst_rate", "GST rate (%)", "number"]];

const RazorpayTab = ({ s, set, save, saving }) => {
  const [testing, setTesting] = useState(false);
  const hook = `${API}/payments/webhook`;
  const test = async () => {
    setTesting(true);
    try { const { data } = await adminApi.post("/admin/razorpay/test"); toast.success(`Connected (${data.mode} mode)${data.webhook_secret_set ? "" : " · webhook secret not set"}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Connection failed"); }
    finally { setTesting(false); }
  };
  const p = { s, set };
  return (
    <Card>
      <div className="space-y-4">
        <Toggle label={`Mode: ${s.razorpay_mode === "live" ? "LIVE" : "TEST"}`} hint="Switch on for Live payments. Keep Test until you have verified a test payment." checked={s.razorpay_mode === "live"} onChange={(v) => set("razorpay_mode", v ? "live" : "test")} testId="razorpay-mode-toggle" />
        <div className="grid gap-4 sm:grid-cols-2">
          <F {...p} k="razorpay_test_key_id" label="Test Key ID" hint="rzp_test_..." /><F {...p} k="razorpay_test_key_secret" label="Test Key Secret" type="password" hint="Stored securely, shown masked" />
          <F {...p} k="razorpay_live_key_id" label="Live Key ID" hint="rzp_live_..." /><F {...p} k="razorpay_live_key_secret" label="Live Key Secret" type="password" />
          <F {...p} k="razorpay_webhook_secret" label="Webhook Secret" type="password" hint="Set the same secret in Razorpay → Webhooks" />
          <Field label="Webhook URL" hint="Events: payment.captured, payment.failed, refund.processed">
            <div className="flex gap-2"><Input readOnly value={hook} testId="razorpay-webhook-url" /><OBtn type="button" className="px-3" onClick={() => { navigator.clipboard.writeText(hook); toast.success("Webhook URL copied"); }} testId="razorpay-webhook-copy"><Copy className="h-4 w-4" /></OBtn></div>
          </Field>
        </div>
        <div className="flex flex-wrap gap-2">
          <YBtn loading={saving} onClick={() => save(["razorpay_mode", "razorpay_test_key_id", "razorpay_test_key_secret", "razorpay_live_key_id", "razorpay_live_key_secret", "razorpay_webhook_secret"])} testId="razorpay-save-btn">Save Razorpay settings</YBtn>
          <OBtn onClick={test} disabled={testing} testId="razorpay-test-btn"><PlugZap className="h-4 w-4" />{testing ? "Testing..." : "Test connection"}</OBtn>
        </div>
        <p className="text-xs text-muted2">Get keys at Razorpay Dashboard → Account & Settings → API Keys. Save first, then test.</p>
      </div>
    </Card>
  );
};

const EmailTab = ({ s, set, save, saving }) => {
  const [to, setTo] = useState("");
  const [sending, run] = useSaver();
  const p = { s, set };
  return (
    <Card>
      <div className="grid gap-4 sm:grid-cols-2">
        <F {...p} k="smtp_host" label="SMTP host" hint="e.g. smtp.gmail.com" /><F {...p} k="smtp_port" label="SMTP port" type="number" />
        <F {...p} k="smtp_user" label="SMTP username" /><F {...p} k="smtp_password" label="SMTP password" type="password" hint="Gmail: use an App Password" />
        <F {...p} k="smtp_from" label="From address" />
        <Toggle label="Use STARTTLS" checked={s.smtp_use_tls} onChange={(v) => set("smtp_use_tls", v)} testId="setting-smtp_use_tls" />
        <Toggle label="Email me on every new lead" checked={s.lead_notify_enabled} onChange={(v) => set("lead_notify_enabled", v)} testId="setting-lead_notify_enabled" />
        <F {...p} k="lead_notify_email" label="Lead notification email" hint="Defaults to business email" />
      </div>
      <div className="mt-5 flex flex-wrap items-end gap-2">
        <YBtn loading={saving} onClick={() => save(["smtp_host", "smtp_port", "smtp_user", "smtp_password", "smtp_from", "smtp_use_tls", "lead_notify_enabled", "lead_notify_email"])} testId="email-save-btn">Save email settings</YBtn>
        <Input className="max-w-xs" placeholder="Send test to..." value={to} onChange={(e) => setTo(e.target.value)} testId="test-email-input" />
        <OBtn disabled={sending || !to} onClick={() => run(() => adminApi.post("/admin/settings/test-email", { to }), "Test email sent")} testId="test-email-btn"><Send className="h-4 w-4" />Send test email</OBtn>
      </div>
    </Card>
  );
};

export function GeneralSettings() {
  const f = useSettingsForm();
  const { s, set, save, saving } = f;
  if (!s) return <Skel rows={8} />;
  const p = { s, set };
  const social = (k, v) => set("social_links", { ...(s.social_links || {}), [k]: v });
  return (
    <>
      <PageTitle title="General Settings" subtitle="Changes update the footer and every legal page instantly.">
        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-ocean bg-white px-5 py-2.5 text-sm font-semibold text-ocean"><ExternalLink className="h-4 w-4" />View legal page</a>
      </PageTitle>
      <Tabs defaultValue="business">
        <TabsList className="mb-4 flex h-auto flex-wrap justify-start rounded-2xl bg-white p-1 shadow-soft">
          {[["business", "Business info"], ["brand", "Branding & SEO"], ["razorpay", "Razorpay"], ["email", "Email (SMTP)"]].map(([k, l]) => <TabsTrigger key={k} value={k} className="rounded-full data-[state=active]:bg-indigo-brand data-[state=active]:text-white" data-testid={`settings-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="business">
          <Card>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {BIZ.map(([k, l, t]) => <F key={k} {...p} k={k} label={l} type={t} />)}
              <Field label="GST display mode"><Select value={s.gst_mode} onChange={(e) => set("gst_mode", e.target.value)} options={[{ value: "exclusive", label: "Prices exclusive of GST" }, { value: "inclusive", label: "Prices inclusive of GST" }]} testId="setting-gst_mode" /></Field>
              <div className="sm:col-span-2 xl:col-span-3"><F {...p} k="address" label="Address" area /></div>
            </div>
            <YBtn loading={saving} onClick={() => save()} className="mt-5" testId="business-save-btn">Save business info</YBtn>
          </Card>
        </TabsContent>
        <TabsContent value="brand">
          <Card>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Logo (replaces text logo)"><ImageField value={s.logo_url} onChange={(v) => set("logo_url", v)} testId="setting-logo" /></Field>
              <Field label="Favicon"><ImageField value={s.favicon_url} onChange={(v) => set("favicon_url", v)} testId="setting-favicon" /></Field>
              <F {...p} k="seo_title" label="Default SEO title" /><F {...p} k="seo_description" label="Default meta description" />
              <Field label="Default OG image" className="sm:col-span-2"><ImageField value={s.og_image} onChange={(v) => set("og_image", v)} testId="setting-og-image" /></Field>
              {["facebook", "instagram", "linkedin", "youtube", "twitter"].map((k) => <Field key={k} label={`${k[0].toUpperCase()}${k.slice(1)} URL`}><Input value={s.social_links?.[k] || ""} onChange={(e) => social(k, e.target.value)} testId={`setting-social-${k}`} /></Field>)}
              <div className="sm:col-span-2"><Toggle label="Require cookie consent" hint="Pixel, GA4, GTM and custom scripts load only after consent" checked={s.require_cookie_consent} onChange={(v) => set("require_cookie_consent", v)} testId="setting-require_cookie_consent" /></div>
            </div>
            <YBtn loading={saving} onClick={() => save()} className="mt-5" testId="brand-save-btn">Save branding</YBtn>
          </Card>
        </TabsContent>
        <TabsContent value="razorpay"><RazorpayTab {...f} /></TabsContent>
        <TabsContent value="email"><EmailTab {...f} /></TabsContent>
      </Tabs>
    </>
  );
}

export function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, run] = useSaver();
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (f.new_password !== f.confirm) return toast.error("Passwords do not match");
    if (await run(() => adminApi.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password }), "Password changed")) setF({ current_password: "", new_password: "", confirm: "" });
  };
  return (
    <>
      <PageTitle title="Change Password" />
      <Card className="max-w-md">
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <Field label="Current password"><Input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} testId="current-password-input" /></Field>
          <Field label="New password" hint="At least 8 characters"><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} testId="new-password-input" /></Field>
          <Field label="Confirm new password"><Input type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} testId="confirm-password-input" /></Field>
          <YBtn type="submit" loading={saving} testId="change-password-submit">Update password</YBtn>
        </form>
      </Card>
    </>
  );
}
